import { Link } from "react-router-dom";
import type { NewsItem } from "../../mocks/news";

/** 竖版图文卡片（用于网格/栏目流） */
export function CoverCard({ item }: { item: NewsItem }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-transform hover:-translate-y-0.5">
      <Link to={`/news/${item.slug}`} className="relative block aspect-[4/3] overflow-hidden">
        <img
          src={item.cover}
          alt={item.title}
          title={item.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2.5 top-2.5 whitespace-nowrap rounded-sm bg-primary-500/95 px-2 py-0.5 text-xs font-medium text-background-50">
          {item.categoryName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-serif text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/news/${item.slug}`} className="line-clamp-2 transition-colors hover:text-primary-600">
            {item.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-700">
          {item.summary}
        </p>
        <div className="mt-3 flex items-center gap-3 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" /> {item.views}
          </span>
          <span>{item.date}</span>
        </div>
      </div>
    </article>
  );
}

/** 横向文字列表行 */
export function ListRow({ item, index }: { item: NewsItem; index?: number }) {
  return (
    <Link
      to={`/news/${item.slug}`}
      className="group flex cursor-pointer items-start gap-3 py-3 first:pt-0 last:pb-0"
    >
      {typeof index === "number" && (
        <span className="mt-0.5 w-5 shrink-0 font-serif text-2xl font-black leading-none text-secondary-400">
          {index < 3 ? (
            <span className="text-primary-500">{String(index + 1).padStart(2, "0")}</span>
          ) : (
            String(index + 1).padStart(2, "0")
          )}
        </span>
      )}
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 md:text-[15px]">
          {item.title}
        </p>
        <p className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" /> {item.views}
          </span>
          <span>{item.date}</span>
        </p>
      </div>
    </Link>
  );
}

/** 横向图文行（分类页/列表） */
export function MediaRow({ item }: { item: NewsItem }) {
  return (
    <article className="group grid grid-cols-1 gap-4 border-b border-background-200 py-6 first:pt-2 last:border-0 sm:grid-cols-[240px_1fr] md:grid-cols-[260px_1fr]">
      <Link to={`/news/${item.slug}`} className="block aspect-[16/10] overflow-hidden rounded-lg">
        <img
          src={item.cover}
          alt={item.title}
          title={item.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-col">
        <span className="inline-flex w-fit whitespace-nowrap rounded-sm bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
          {item.categoryName}
        </span>
        <h3 className="mt-2 font-serif text-lg font-bold leading-snug text-foreground-950 md:text-xl">
          <Link to={`/news/${item.slug}`} className="line-clamp-2 transition-colors hover:text-primary-600">
            {item.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-700">{item.summary}</p>
        <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-3-line" /> {item.author}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line" /> 阅读约 {item.readMinutes} 分钟
          </span>
          <span>{item.date}</span>
        </div>
      </div>
    </article>
  );
}

/** 面包屑 */
export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
      <i className="ri-home-4-line text-foreground-500" />
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <i className="ri-arrow-right-s-line opacity-50" />}
            {last || !item.to ? (
              <span className={last ? "font-medium text-foreground-950" : "text-foreground-600"} aria-current={last ? "page" : undefined}>
                {item.label}
              </span>
            ) : (
              <Link to={item.to} className="whitespace-nowrap transition-colors hover:text-primary-600">
                {item.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}